import type {
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellMouseOverEvent,
  ColDef,
  Column,
  ColumnMovedEvent,
  ColumnResizedEvent,
  GridApi,
  GridOptions,
  IRowNode,
  IServerSideDatasource,
  ProcessUnpinnedColumnsParams,
  SortChangedEvent,
} from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import type { Mode } from '@/types/enums/mode'

import 'ag-grid-enterprise/styles/ag-grid.css'
import 'ag-grid-enterprise/styles/ag-theme-quartz.css'

export enum TableDataModel {
  ServerSide = 'serverSide',
  ClientSide = 'clientSide',
}

export enum TableSize {
  Full = 'full',
  Compact = 'compact',
}

// This should go away - using this removes type safety.
// We're giving default any here (ColDef<TData = any, TValue = any>)
export type ColumnDefinition = ColDef

interface CheckboxState {
  selectedRowIds: Set<string>
  headerCheckboxChecked: boolean | 'indeterminate'
}

export interface TableContext {
  tableSize: TableSize
  checkboxState: CheckboxState
}

interface TableIdConfig {
  clientSideField?: string // The field to use for client-side data
  serverSideField?: string // The field to use for server-side data
  fallback?: string // Fallback field if neither is specified
}

export interface TableProps<TData> extends AgGridReactProps<TData> {
  /** Size of the table. */
  tableSize: TableSize
  /** Set custom row height. Set to false on default. This overrides the tableSize row heights and should only be used for specific tables. */
  customRowHeight?: number | false
  /** Optionally override the header height for this table only. */
  headerHeight?: number
  /** Whether to use the server-side or client-side instance. */
  gridModel: TableDataModel
  /** Data to be displayed in the table. */
  data?: TData[]
  /** Column definitions for the table. */
  columnDefs: ColumnDefinition[]
  /** Whether to enable checkbox selection. */
  checkboxSelection?: boolean
  /** Callback function that is used to fetch data from the server. */
  createServerSideDatasource?: () => IServerSideDatasource
  /** Callback function that is called when a cell is clicked. */
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  onCellClicked?: (event: CellClickedEvent<TData, any>) => void
  /** Callback function that is called when a cell is hovered. */
  onCellHover?: (event: CellMouseOverEvent) => void
  /** Callback function that is called when a column is moved. */
  onColumnMoved?: (event: ColumnMovedEvent) => void
  /** Callback function that is called when a column is resized. */
  onColumnResized?: (event: ColumnResizedEvent) => void
  /** Callback function that is called when selected rows change. */
  onSelectedRowsChange?: (selectedIds: Set<string>) => void
  /** Search term to filter the table client-side. */
  searchTerm?: string
  /** AG Grid options. */
  gridOptions?: GridOptions
  /** Whether the data is being loaded. */
  loading?: boolean
  /** Whether to use the transaction API to update the table. */
  useTransactionAPI?: boolean
  /** Whether pagination is enabled. */
  pagination?: boolean
  /** When true, AG Grid will log helpful debug information to the console. */
  debug?: boolean
  /** Triggers the onExternalUpdate callback to be called each time this value is incremented. */
  externalUpdateVersion?: number
  /** Callback function that is called when externalUpdateVersion changes. */
  onExternalUpdate?: (gridApi: GridApi) => void
  /** Mode of the table. */
  mode?: Mode
  /** Field to use as the row id. Defaults to 'id' for client-side and 'isin' for server-side. */
  idConfig?: TableIdConfig
  /** Callback function that is called to determine if the checkbox should be hidden. */
  isCheckboxDisabled?: (params: IRowNode) => boolean
  /** Callback function that is called to get tooltip text for disabled checkboxes. */
  getCheckboxTooltip?: (params: IRowNode) => string | undefined
  /** Callback function that is called when a cell value is changed. */
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  onCellValueChanged?: (event: any) => void
  /** Callback function that is called when cell editing stops. */
  onCellEditingStopped?: (event: CellEditingStoppedEvent) => void
  /** Grouping default expanded level. */
  groupDefaultExpanded?: number
  /** Group header height. */
  groupHeaderHeight?: number
  /** Callback function that is called when the sort changes. */
  onSortChanged?: (event: SortChangedEvent) => void
  /** Whether to suppress column virtualisation. */
  suppressColumnVirtualisation?: boolean
  /** Whether to supporess CSV and Excel export from context menu. */
  suppressExportFromContextMenu?: boolean
  /** Whether to suppress the aggregation function in the header. */
  suppressAggFuncInHeader?: boolean
  /** Whether to suppress the full width loading row for server-side tables. */
  suppressServerSideFullWidthLoadingRow?: boolean
  /** Whether to show the filter buttons in the header. */
  showHeaderFilterButtons?: boolean
  /** Whether to hide the select all checkbox in the header completely. */
  hideSelectAll?: boolean
  /** Whether to process unpinned columns. */
  processUnpinnedColumns?: (
    params: ProcessUnpinnedColumnsParams<TData>,
  ) => Column[]
}
